import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import ColumnCell from "../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/column-cell";
import ColumnEmployeeCell from "../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/column-employee-cell";
import ColumnHeader from "../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/column-header";
import ColumnSkillCell from "../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/column-skill-cell";
import ColumnSkillHeader from "../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/column-skill-header";
import { formatDate, formatSkillScore } from "../../../../ts/utils/helpers";
import { HIDDEN_MATRIX_COLUMNS } from "../../../../ts/utils/matrix-columns";
import { formatSkillsByJobs } from "../../../utils/helpers";

const useCrossSkillColumn = (
	filteredJobs,
	allSkills,
	allSkillLevels,
	handleSkillLevels,
	handleAddSkillInterested,
	searchText,
	appliedFilters,
	isWorkerSubTypeEnabled
) => {
	const { t } = useTranslation();
	const filterState = useMemo(() => {
		const jobFilter = appliedFilters.find((f) => f.key === "jobs");
		const skillFilter = appliedFilters.find((f) => f.key === "skills");
		return {
			isJobFilter: jobFilter?.data?.length > 0 ?? false,
			isSkillFilter: skillFilter?.data?.length > 0 ?? false,
			jobIds: jobFilter?.data?.map((job) => job.id) ?? [],
			skillIds: skillFilter?.data?.map((skill) => skill.id) ?? [],
		};
	}, [appliedFilters]);

	const { jobIdsSkillsArr, filteredSkills } = useMemo(() => {
		const filteredSkills = filterState.isSkillFilter
			? allSkills.filter((skill) => filterState.skillIds.includes(skill.id))
			: [];

		const preJobIdsSkillsArr = filterState.isJobFilter
			? []
			: filterState.isSkillFilter
			? filteredSkills
			: allSkills;

    const jobIdsSkillsArr = [...preJobIdsSkillsArr]?.sort(
      (skill1, skill2) => skill1.name?.trim().localeCompare(skill2.name?.trim())
    ) ?? [];

		if (filterState.isJobFilter) {
			formatSkillsByJobs(filteredJobs, jobIdsSkillsArr, filterState.jobIds);
		}

		return { jobIdsSkillsArr, filteredSkills };
	}, [filterState, allSkills, filteredJobs]);

	const baseColumns = useMemo(() => {
		const numericComparator = (v1, v2) => {
			const getNumericPart = (value) => {
				if (!value) return null;
				const match = value.trim().match(/^\d+$/);
				return match ? parseInt(match[0], 10) : null;
			};

			if (!v1?.trim() && !v2?.trim()) return 0;
			if (!v1?.trim()) return 1;
			if (!v2?.trim()) return -1;

			const num1 = getNumericPart(v1);
			const num2 = getNumericPart(v2);

			if (num1 !== null && num2 !== null) return num1 - num2;
			if (num1 !== null) return -1;
			if (num2 !== null) return 1;
			return v1.localeCompare(v2);
		};

		const createBaseColumn = (
			field,
			headerName,
			width = 200,
			customProps = {}
		) => ({
			field,
			headerName,
			width,
			headerAlign: "center",
			renderHeader: () => <ColumnHeader text={headerName} />,
			renderCell: (params) => (
				<ColumnCell text={params.row[field] ?? "-"} searchText={searchText} />
			),
			valueGetter: (params) => params.row[field],
			...customProps,
		});

		const columns = [
			createBaseColumn("companyEmployeeId", t("cross-skilling_worker-id"), 200, {
				sortComparator: numericComparator,
				order: 4,
			}),
			createBaseColumn("jobTitle", t("cross-skilling_job-title"), 200, {
				order: 5,
			}),
			{
				...createBaseColumn("name", t("cross-skilling_worker-name"), 200, {
					valueGetter: (params) =>
						`${params.row.firstName} ${params.row.lastName}`,
					order: 6,
				}),
				renderCell: (params) => (
					<ColumnEmployeeCell params={params} searchText={searchText} />
				),
			},
			createBaseColumn("workerType", t("cross-skilling_worker-type"), 200, {
				order: 6,
			}),
			createBaseColumn("managerName", t("cross-skilling_reporting-manager"), 200, {
				valueGetter: (params) => params.row.managerName?.toLowerCase(),
				order: 7,
			}),
			createBaseColumn("primaryJob", t("cross-skilling_primary-job"), 200, {
				order: 8,
			}),
			createBaseColumn("workCenter", t("cross-skilling_work-center"), 200, {
				order: 9,
			}),
			createBaseColumn("location", t("cross-skilling_location"), 200, {
				order: 10,
			}),
			{...createBaseColumn("skillsCoverage",
        "Skills Coverage",
        200,
        {
          order: 10,
          type: "number",
          valueGetter: (params) => {
            const rawValue = params.row?.skillsCoverage;
            if (!rawValue) return 0;
            return parseFloat(rawValue);
          }
        },
      )},
			createBaseColumn("shiftPatternName", "Shift Pattern", 200, { order: 10 }),
			{
				...createBaseColumn("skillScore", "Total Skill Score", 160, {
					order: 10,
				}),
				renderCell: (params) => (
					<ColumnCell
						text={formatSkillScore(params.row.skillScore) ?? "-"}
						searchText={searchText}
					/>
				),
			},
			{
				...createBaseColumn("joiningDate", t("cross-skilling_joining-date"), 200, {
					order: 11,
				}),
				valueGetter: (params) => {
					return new Date(params.row.joiningDate).getTime();
				},
				renderCell: (params) => {
					const joiningDate = formatDate({
						dateStr: params?.row?.joiningDate,
						formatType: "NUMERIC_DATE",
						ISOString: false,
					});
					return (
						<ColumnCell text={joiningDate ?? "-"} searchText={searchText} />
					);
				},
			},
		];

		if (isWorkerSubTypeEnabled) {
			columns.splice(
				4,
				0,
				createBaseColumn("workerSubTypeText", t("cross-skilling_worker-sub-type"), 200, {
					order: 6,
				})
			);
		}

		return columns.filter((column) => !HIDDEN_MATRIX_COLUMNS.includes(column.field));
	}, [searchText, isWorkerSubTypeEnabled, t]);

	const skillColumns = useMemo(
		() =>
			jobIdsSkillsArr.map((skill, idx) => ({
				field: filterState.isJobFilter
					? `${skill.id}${skill.jobColor}`
					: skill.id.toString(),
				headerName: skill.name,
				width: 100,
				type: "actions",
				headerAlign: "center",
				sortable: true,
				renderCell: (params) => (
					<ColumnSkillCell
						skill={
							filterState.isJobFilter ? { ...skill, id: skill.skillId } : skill
						}
						params={params}
						handleSkillLevels={handleSkillLevels}
						allSkillLevels={allSkillLevels}
						handleAddSkillInterested={handleAddSkillInterested}
					/>
				),
				renderHeader: () => (
					<ColumnSkillHeader
						skill={skill}
						isJobFilter={filterState.isJobFilter}
						searchText={searchText}
					/>
				),
				valueGetter: (params) => {
					const skillInCell = params.row.skills.find(
						(x) => x.skillId === parseInt(skill.skillId)
					);
					return skillInCell ? skillInCell.level + 2 : 1;
				},
				order: 11 + idx,
			})),
		[
			jobIdsSkillsArr,
			filterState.isJobFilter,
			handleSkillLevels,
			handleAddSkillInterested,
			allSkillLevels,
			searchText,
		]
	);

	return { columns: [...baseColumns, ...skillColumns] };
};

export default useCrossSkillColumn;
